import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { AuthCallback } from "@/components/AuthCallback";
import { PageSkeleton } from "@/components/Loader";
import { prefetch } from "@/lib/prefetch";
import Home from "@/pages/Home";

const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));
const Toaster = named(() => import("@/components/ui/sonner"), "Toaster");
const PRELOAD = { "/dashboard": () => import("@/pages/Dashboard"), "/holistic-report": () => import("@/pages/HolisticReport") };
if (window.location.pathname !== "/" && !window.location.hash.includes("session_id=")) {
  import("@/components/ProtectedRoute");
  import("@/components/AppShell");
  PRELOAD[window.location.pathname]?.();
  if (window.location.pathname === "/dashboard") prefetch("summary", "/dashboard/summary");
}
const ProtectedRoute = named(() => import("@/components/ProtectedRoute"), "ProtectedRoute");
const AppShell = named(() => import("@/components/AppShell"), "AppShell");
const ContentPage = lazy(() => import("@/pages/ContentPage"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminUserReport = lazy(() => import("@/pages/admin/AdminUserReport"));
const AdminFinancial = lazy(() => import("@/pages/admin/AdminFinancial"));
const AdminFutureReadiness = lazy(() => import("@/pages/admin/AdminFutureReadiness"));
const AdminIndia = lazy(() => import("@/pages/admin/AdminIndia"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/AdminSubscriptions"));
const AdminNotifications = lazy(() => import("@/pages/admin/AdminNotifications"));
const AdminBranding = named(() => import("@/pages/admin/AdminBranding"), "AdminBranding");
const AdminLinks = named(() => import("@/pages/admin/AdminBranding"), "AdminLinks");
const AdminContent = named(() => import("@/pages/admin/AdminMisc"), "AdminContent");
const AdminUploads = named(() => import("@/pages/admin/AdminMisc"), "AdminUploads");
const AdminAudit = named(() => import("@/pages/admin/AdminMisc"), "AdminAudit");
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Profile = lazy(() => import("@/pages/Profile"));
const Assets = lazy(() => import("@/pages/Assets"));
const Liabilities = lazy(() => import("@/pages/Liabilities"));
const Review = lazy(() => import("@/pages/Review"));
const Certificates = lazy(() => import("@/pages/Certificates"));
const FutureReadiness = lazy(() => import("@/pages/FutureReadiness"));
const HolisticReport = lazy(() => import("@/pages/HolisticReport"));

const shell = (el, requireOnboarded = true, requireAdmin = false) => (
  <Suspense fallback={<PageSkeleton full />}>
    <ProtectedRoute requireOnboarded={requireOnboarded} requireAdmin={requireAdmin}>
      <AppShell><Suspense fallback={<PageSkeleton />}>{el}</Suspense></AppShell>
    </ProtectedRoute>
  </Suspense>
);

const pub = (page) => <Suspense fallback={<PageSkeleton full />}><ContentPage page={page} /></Suspense>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/how-it-works" element={pub("how_it_works")} />
      <Route path="/privacy" element={pub("privacy")} />
      <Route path="/terms" element={pub("terms")} />
      <Route path="/disclaimer" element={pub("disclaimer")} />
      <Route path="/admin" element={shell(<AdminDashboard />, true, true)} />
      <Route path="/admin/users" element={shell(<AdminUsers />, true, true)} />
      <Route path="/admin/users/:userId" element={shell(<AdminUserReport />, true, true)} />
      <Route path="/admin/financial" element={shell(<AdminFinancial />, true, true)} />
      <Route path="/admin/future-readiness" element={shell(<AdminFutureReadiness />, true, true)} />
      <Route path="/admin/branding" element={shell(<AdminBranding />, true, true)} />
      <Route path="/admin/links" element={shell(<AdminLinks />, true, true)} />
      <Route path="/admin/subscriptions" element={shell(<AdminSubscriptions />, true, true)} />
      <Route path="/admin/notifications" element={shell(<AdminNotifications />, true, true)} />
      <Route path="/admin/india" element={shell(<AdminIndia />, true, true)} />
      <Route path="/admin/content" element={shell(<AdminContent />, true, true)} />
      <Route path="/admin/uploads" element={shell(<AdminUploads />, true, true)} />
      <Route path="/admin/audit" element={shell(<AdminAudit />, true, true)} />
      <Route path="/onboarding" element={shell(<Onboarding />, false)} />
      <Route path="/dashboard" element={shell(<Dashboard />)} />
      <Route path="/profile" element={shell(<Profile />)} />
      <Route path="/assets" element={shell(<Assets />)} />
      <Route path="/liabilities" element={shell(<Liabilities />)} />
      <Route path="/review" element={shell(<Review />)} />
      <Route path="/certificates" element={shell(<Certificates />)} />
      <Route path="/future-readiness" element={shell(<FutureReadiness />)} />
      <Route path="/holistic-report" element={shell(<HolisticReport />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Suspense fallback={null}><Toaster position="top-center" richColors /></Suspense>
    </AuthProvider>
  );
}
